import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import type { TEIFilter } from '@timeedit/ui-components';
import type { TSavedFilter } from '@timeedit/ui-components/components/Filter/Filter.type';
import type { TRootState } from './store';

/**
 * This is a persisted slice for storing saved filters.
 *
 * Filters are organized per organization and table.
 */
const slice = createSlice({
  name: 'persistedFilters',
  initialState: {} as TState,
  reducers: {
    saveFilter: {
      prepare: (
        organizationId: string,
        tableId: string,
        filterName: string,
        filter: TEIFilter
      ) => {
        return {
          payload: {
            organizationId,
            tableId,
            filter: {
              key: uuidv4(),
              label: filterName.trim(),
              filter,
            },
          },
        };
      },
      reducer: (
        state,
        {
          payload: { organizationId, tableId, filter },
        }: TableAction<{ filter: TSavedFilter }>
      ) => {
        if (filter.label === '') {
          return; // Do not save filters with empty labels
        }
        state[organizationId] ??= {};
        state[organizationId][tableId] ??= { savedFilters: [] };
        state[organizationId][tableId].savedFilters.push(filter);
      },
    },
    deleteFilter: (
      state,
      {
        payload: { organizationId, tableId, filterKey },
      }: TableAction<{ filterKey: string }>
    ) => {
      const table = state[organizationId]?.[tableId];
      if (!table) {
        return;
      }
      table.savedFilters = table.savedFilters.filter(
        (f) => f.key !== filterKey
      );

      // Trim empty config
      if (table.savedFilters.length === 0) {
        delete state[organizationId][tableId];
      }
      if (Object.keys(state[organizationId]).length === 0) {
        delete state[organizationId];
      }
    },
  },
});

export const { saveFilter, deleteFilter } = slice.actions;

export const createFilterSelector = () => {
  return createSelector(
    (state: TRootState) => state.persistedFilters,
    (_: TRootState, organizationId: string | undefined) => organizationId,
    (_: TRootState, __: string, tableId: string) => tableId,
    (persistedFilters, organizationId, tableId) => {
      if (!organizationId) return [];
      return persistedFilters[organizationId]?.[tableId]?.savedFilters ?? [];
    }
  );
};

type TState = {
  [organizationId: string]: {
    [tableId: string]: {
      savedFilters: TSavedFilter[];
    };
  };
};

type TableAction<T extends object> = PayloadAction<
  { organizationId: string; tableId: string } & T
>;

export default slice.reducer;
